import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CreditCard, Landmark, RefreshCw } from 'lucide-react';
import { currentMembership, useHousehold } from '@/lib/queries';
import { api, ApiError } from '@/lib/api';
import { date, money } from '@/lib/format';
import { ErrorNote, Spinner } from '@/components/Layout';

interface QuoteLine { person_id: string; name: string; plan_code: string; band: string; starts_on: string; ends_on: string; price_pence: number }
interface Quote { lines: QuoteLine[]; total_pence: number; family: boolean; individual_total_pence: number }

const PLAN_LABEL: Record<string, string> = { junior: 'Junior', adult: 'Adult', family: 'Family', family_extra_adult: 'Family, extra adult' };

/** Choose who to cover, see the price the server works out, and go to GoCardless to pay. */
export function Renew() {
  const household = useHousehold();
  const [selected, setSelected] = useState<string[] | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [busy, setBusy] = useState<'' | 'direct_debit' | 'pay_by_bank' | 'paypal'>('');
  const [error, setError] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
  const people = household.data?.people ?? [];
  // Pre-tick anyone with no membership, an expired one, or one ending within 30 days.
  const needing = useMemo(() => people.filter(p => !p.frozen_at).filter(p => { const m = currentMembership(p); return !m || m.status !== 'active' || m.ends_on <= soon; }).map(p => p.id), [people, soon]);
  const chosen = selected ?? needing;

  useEffect(() => {
    if (!chosen.length) { setQuote(null); return; }
    let live = true;
    setQuoteError('');
    api<Quote>('/checkout/quote', { method: 'POST', body: { person_ids: chosen } })
      .then(q => {
        if (!live) return;
        setQuote(q);
        // Pricing clears a checkout left unfinished, so show its people as free to pay again.
        if (people.some(p => chosen.includes(p.id) && currentMembership(p)?.status === 'pending')) void household.refetch();
      })
      .catch(e => { if (live) { setQuote(null); setQuoteError(e instanceof ApiError && e.status === 409 ? 'A payment for someone here was started in the last half hour. If you left the payment page without finishing, you can try again 30 minutes after you started.' : 'We could not work out the price. Please refresh the page.'); } });
    return () => { live = false; };
  }, [chosen.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (household.isLoading) return <Spinner />;
  if (!household.data) return <Navigate to="/join" replace />;

  async function pay(method: 'direct_debit' | 'pay_by_bank' | 'paypal') {
    setBusy(method); setError('');
    try {
      const r = await api<{ url: string }>('/checkout/membership', { method: 'POST', body: { person_ids: chosen, method } });
      window.location.assign(r.url);
    } catch {
      setError('We could not start the payment. Please try again in a moment.');
      setBusy('');
    }
  }

  const toggle = (id: string) => people.find(p => p.id === id)?.frozen_at ? undefined : setSelected(chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id]);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/home" className="text-sm text-ink-500 hover:underline">← My household</Link>
        <h1 className="mt-2 text-2xl font-bold">Choose memberships</h1>
        <p className="mt-1 text-sm text-ink-500">Tick everyone who needs a membership. We'll use the family price when it's cheaper.</p>
      </div>

      <ul className="card divide-y divide-stone-100 p-0">
        {people.map(p => {
          const m = currentMembership(p);
          const active = m && m.status === 'active' && m.ends_on >= today;
          return (
            <li key={p.id}>
              <label className="flex cursor-pointer items-center gap-3 px-5 py-4">
                <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={chosen.includes(p.id)} disabled={!!p.frozen_at} onChange={() => toggle(p.id)} />
                <span className="flex-1">
                  <span className="block font-medium">{p.first_name} {p.last_name}</span>
                  <span className="block text-sm text-ink-500">{p.frozen_at ? 'Membership paused. Please contact the Membership Secretary.' : active ? `Member until ${date(m!.ends_on)}: renewing adds a year from then` : m?.status === 'pending' ? 'Payment in progress' : 'Not currently a member'}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {quoteError && <ErrorNote>{quoteError}</ErrorNote>}

      {quote && (
        <div className="card space-y-3">
          {quote.lines.map(l => (
            <div key={l.person_id} className="flex justify-between gap-3 text-sm">
              <span>{l.name} <span className="text-ink-500">· {PLAN_LABEL[l.plan_code] ?? l.plan_code} · {date(l.starts_on)} to {date(l.ends_on)}</span></span>
              <span className="font-medium">{l.price_pence ? money(l.price_pence) : 'Included'}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-stone-200 pt-3 font-semibold">
            <span>Total{quote.family && <span className="ml-2 font-normal text-emerald-700">Family price saves {money(quote.individual_total_pence - quote.total_pence)}</span>}</span>
            <span>{money(quote.total_pence)}</span>
          </div>
        </div>
      )}

      {quote && (
        <div className="grid gap-3 sm:grid-cols-2">
          <button className="card text-left transition hover:border-brand-500 disabled:opacity-50" disabled={!!busy} onClick={() => pay('direct_debit')}>
            <RefreshCw className="text-brand-600" size={20} />
            <p className="mt-2 font-semibold">{busy === 'direct_debit' ? 'Opening GoCardless…' : 'Pay now and renew automatically'}</p>
            <p className="mt-1 text-sm text-ink-500">Pay {money(quote.total_pence)} from your bank today, and set up a Direct Debit so next year renews itself. We'll email you before it does.</p>
            <p className="mt-2 text-xs font-semibold text-brand-700">Recommended</p>
          </button>
          <button className="card text-left transition hover:border-brand-500 disabled:opacity-50" disabled={!!busy} onClick={() => pay('pay_by_bank')}>
            <Landmark className="text-brand-600" size={20} />
            <p className="mt-2 font-semibold">{busy === 'pay_by_bank' ? 'Opening GoCardless…' : 'Pay once'}</p>
            <p className="mt-1 text-sm text-ink-500">Pay {money(quote.total_pence)} from your bank today. We'll remind you when it's time to renew.</p>
          </button>
          <button className="card text-left transition hover:border-brand-500 disabled:opacity-50 sm:col-span-2" disabled={!!busy} onClick={() => pay('paypal')}>
            <CreditCard className="text-brand-600" size={20} />
            <p className="mt-2 font-semibold">{busy === 'paypal' ? 'Opening PayPal…' : 'Pay with PayPal or card'}</p>
            <p className="mt-1 text-sm text-ink-500">Pay {money(quote.total_pence)} with your PayPal account, or by debit or credit card without one. We'll remind you when it's time to renew.</p>
          </button>
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      <p className="text-xs text-ink-500">Payments are handled by GoCardless or PayPal. Your bank or card details go to them, never to EGKA's website.</p>
    </div>
  );
}
